import type { InfoGridField } from 'v2/components/InfoGrid/types';

import type {
    CertificationFieldLabels,
    CompanyFieldLabels,
    ContactFieldLabels,
    Organization,
    PersonOfContact
} from './types';

export const TEST_ID = 'organizationDetailsSection';

const PHONE_PATTERN = /^(\d{3})(\d{3})(\d{4})$/;

const AREA_CODE_GROUP = 1;

const PREFIX_GROUP = 2;

const LINE_NUMBER_GROUP = 3;

export const formatPhoneNumber = (phoneNumber?: string | null): string => {
    if (!phoneNumber) {
        return '';
    }

    const phoneSections = phoneNumber.match(PHONE_PATTERN);

    if (phoneSections === null) {
        return phoneNumber;
    }

    return `(${phoneSections[AREA_CODE_GROUP]}) ${phoneSections[PREFIX_GROUP]}-${phoneSections[LINE_NUMBER_GROUP]}`;
};

export const formatAddress = (address1?: string | null, address2?: string | null): string =>
    [address1, address2].filter(Boolean).join(', ');

const toInfoGridFields = (
    fields: { fullWidth?: boolean; label: string; value: string }[],
    notProvidedText: string
): InfoGridField[] =>
    fields.map(({ fullWidth, label, value }) => ({
        empty: !value,
        fullWidth,
        label,
        value: value || notProvidedText
    }));

export const buildCompanyFields = (
    organization: Organization | undefined,
    labels: CompanyFieldLabels,
    notProvidedText: string
): InfoGridField[] =>
    toInfoGridFields(
        [
            { fullWidth: true, label: labels.companyName, value: organization?.name ?? '' },
            {
                fullWidth: true,
                label: labels.address,
                value: formatAddress(organization?.address1, organization?.address2)
            },
            { label: labels.city, value: organization?.city ?? '' },
            { label: labels.state, value: organization?.state ?? '' },
            { label: labels.zip, value: organization?.zipCode ?? '' },
            { label: labels.phone, value: formatPhoneNumber(organization?.phoneNumber) }
        ],
        notProvidedText
    );

export const buildContactFields = (
    personOfContact: PersonOfContact,
    labels: ContactFieldLabels,
    notProvidedText: string
): InfoGridField[] => {
    const firstName = personOfContact?.firstName ?? '';
    const lastName = personOfContact?.lastName ?? '';

    return toInfoGridFields(
        [
            {
                fullWidth: true,
                label: labels.contactName,
                value: [firstName, lastName].filter(Boolean).join(' ')
            },
            { label: labels.phone, value: formatPhoneNumber(personOfContact?.phoneNumber) },
            { label: labels.email, value: personOfContact?.email ?? '' }
        ],
        notProvidedText
    );
};

export const buildCertificationFields = (
    organization: Organization | undefined,
    labels: CertificationFieldLabels,
    notProvidedText: string
): InfoGridField[] =>
    toInfoGridFields(
        [
            { label: labels.faaDocketNumber, value: organization?.faaDocketNumber ?? '' },
            { label: labels.faaExemptionNumber, value: organization?.faaExemptionNumber ?? '' },
            {
                fullWidth: true,
                label: labels.certificateOfAuthorizationNumber,
                value: organization?.coa ?? ''
            }
        ],
        notProvidedText
    );
